import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { IAMService } from '@vendasta/iam';
import {
  ListSalespersonsRequestListSalespersonsFiltersInterface,
  PagedResponse,
  Salesperson,
  SalespersonService,
} from '@vendasta/sales';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ASSIGNED_SALESPERSON } from '../../../../data-services/users.service';

@Component({
  selector: 'automata-salesperson-selector',
  templateUrl: './salesperson-selector.component.html',
  styleUrls: ['./salesperson-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SalespersonSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SalespersonSelectorComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyTooltipModule,
    MatTooltipModule,
    GalaxyInfiniteScrollTriggerModule,
  ],
})
export class SalespersonSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  defaultSalesPersonOption = [
    {
      salespersonId: ASSIGNED_SALESPERSON,
      firstName: this.translateService.instant('AUTOMATIONS.COMMON.ASSIGNED_SALESPERSON'),
    } as Salesperson,
  ];
  salespersonOptions$$ = new BehaviorSubject<Salesperson[]>(this.defaultSalesPersonOption);
  cursor = '';
  pageSize = 25;
  hasMore = true;

  salespersonControl = new FormControl<string>('', Validators.required);
  destroySubject = new Subject<void>();

  loadMore$$ = new BehaviorSubject<null>(null);
  loading: boolean;

  @Input() marketId = '';
  @Input() partnerId = '';

  constructor(
    private readonly userLookupService: IAMService,
    private readonly cdr: ChangeDetectorRef,
    private readonly salespersonService: SalespersonService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    firstValueFrom(this.doLookup('')).then((users) => {
      this.loading = true;
      this.updateSalespersonOptions(this.defaultSalesPersonOption, users.results, users.nextCursor, users.hasMore);
    });
    this.loadMore$$
      .pipe(
        switchMap(() => {
          this.loading = true;
          this.cdr.detectChanges();
          if (typeof this.salespersonControl.value !== 'string') return;
          return this.doLookup(this.salespersonControl.value);
        }),
        withLatestFrom(this.salespersonOptions$$),
      )
      .subscribe(([resp, currentSalespersonOptions]) => {
        this.updateSalespersonOptions(currentSalespersonOptions, resp.results, resp.nextCursor, resp.hasMore);
      });
    this.salespersonOptions$$.next(this.defaultSalesPersonOption);
    this.salespersonControl.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe((input) => {
      if (typeof input === 'string') {
        this.cursor = '';
        this.hasMore = true;
        this.loading = true;
        firstValueFrom(this.doLookup(input)).then((users) => {
          this.updateSalespersonOptions(this.defaultSalesPersonOption, users.results, users.nextCursor, users.hasMore);
        });
      }
    });
  }

  doLookup(_searchTerm: string): Observable<PagedResponse<Salesperson>> {
    const listParams: ListSalespersonsRequestListSalespersonsFiltersInterface = {
      partnerId: this.partnerId,
    };

    return this.salespersonService.listSalespersons(listParams, this.cursor, this.pageSize);
  }

  registerOnChange(fn: any): void {
    this.salespersonControl.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.salespersonControl.valueChanges.pipe(takeUntil(this.destroySubject)).subscribe(fn);
  }

  writeValue(salesperson: string): void {
    this.salespersonControl.patchValue(salesperson, { emitEvent: false });
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return !this.salespersonControl.invalid ? null : { invalid: true };
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.salespersonControl.disable() : this.salespersonControl.enable();
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  loadMore(): void {
    if (!this.hasMore) {
      return;
    }

    this.loadMore$$.next(null);
  }

  updateSalespersonOptions(
    currentSalespersonOptions: Salesperson[],
    newSalespersonOptions: Salesperson[],
    nextCursor: string,
    hasMore: boolean,
  ): void {
    this.salespersonOptions$$.next(currentSalespersonOptions.concat(newSalespersonOptions));
    this.cursor = nextCursor;
    this.hasMore = hasMore;
    this.loading = false;
    this.cdr.detectChanges();
  }

  displayFn(user: Salesperson): string {
    return user ? user.firstName + ' ' + user.lastName : '';
  }
}
